/* eslint-disable no-nested-ternary */
import './SessionList.css';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import { toGroup } from '@icp/utils';
import { formatDate, formatTime } from '@icp/i18n';
import { Fragment, useState } from 'react';
import Icon from '../../Icon';
import {
  deleteSession,
  fetchSessionList,
  selectSessionList,
  selectSessionListLoaded,
  setCurrentSession,
  useDispatch,
  useSelector,
} from '../store';
import { SESSION_DATE_CATEGORY } from '../consts';

const toGroupByCategory = toGroup((x) => x.dateCategory);

function SessionList(props) {
  const { t } = useTranslation(['icp-components']);
  const dispatch = useDispatch();
  const sessionListLoaded = useSelector(selectSessionListLoaded);
  const sessionList = useSelector(selectSessionList);
  const { handleSessionSelected, isCurrentAgentList, currentAgentId } = props;

  if (!sessionListLoaded) {
    throw new Promise((resolve) => {
      dispatch(fetchSessionList()).finally(resolve);
    });
  }

  // 是否只展示当前agent的session列表
  const filteredList = isCurrentAgentList
    ? sessionList.filter(
        (s) => !s?.assistantId || String(s?.assistantId) === String(currentAgentId),
      )
    : sessionList;
  const group = toGroupByCategory(filteredList);

  const [search, setSearch] = useState('');

  return (
    <div className="ai-agent-session-list icp-thin-scrollbar">
      <Input
        key="search-box"
        prefix={<Icon name="oct:search" />}
        placeholder={t('ai-agent.search-session-placeholder', { ns: 'icp-components' })}
        style={{
          height: 32,
          borderRadius: 16,
          border: 'none',
        }}
        allowClear={true}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {group.map(([category, list]) => (
        <Fragment key={category}>
          <SessionCategoryTitle key={category} category={category} />
          {list
            .filter((session) =>
              !search ? true : (session.title || '').toLowerCase().includes(search.toLowerCase()),
            )
            .map((session) => (
              <SessionItem
                key={`${session.id}`}
                session={session}
                handleSessionSelected={handleSessionSelected}
              />
            ))}
        </Fragment>
      ))}
    </div>
  );
}

function SessionCategoryTitle({ category }) {
  const { t } = useTranslation('icp-common');

  return (
    <div className="ai-agent-session-category">
      {category === SESSION_DATE_CATEGORY.TODAY
        ? t('today', { ns: 'icp-common' })
        : category === SESSION_DATE_CATEGORY.THIS_WEEK
          ? t('this-week', { ns: 'icp-common' })
          : category === SESSION_DATE_CATEGORY.THIS_YEAR
            ? t('this-year', { ns: 'icp-common' })
            : t('long-ago', { ns: 'icp-common' })}
    </div>
  );
}

SessionCategoryTitle.propTypes = {
  category: PropTypes.oneOf(Object.values(SESSION_DATE_CATEGORY)),
};

function SessionItem({ session, handleSessionSelected }) {
  const dispatch = useDispatch();

  const onSelect = () => {
    dispatch(setCurrentSession(session));
    handleSessionSelected?.(session);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    dispatch(deleteSession(session));
  };

  const dateText =
    {
      [SESSION_DATE_CATEGORY.TODAY]: (date) => formatTime(date, { timeStyle: 'short' }),
      [SESSION_DATE_CATEGORY.THIS_WEEK]: (date) => formatDate(date, { weekday: 'long' }),
      [SESSION_DATE_CATEGORY.THIS_YEAR]: (date) =>
        formatDate(date, { month: 'short', day: 'numeric' }),
      [SESSION_DATE_CATEGORY.OTHER]: (date) =>
        formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' }),
    }[session.dateCategory]?.(new Date(session.date)) ?? new Date(session.date).toISOString();

  return (
    <div onClick={onSelect} className="ai-agent-session-item">
      <div className="title text-ellipsis">{session.title}</div>
      <div className="right">
        <div className="date">{dateText}</div>
        <div className="action">
          <button className="icp-action-button" onClick={handleDelete}>
            <Icon name="oct:trash" size={16} style={{ color: '#ff4d4f' }} />
          </button>
        </div>
      </div>
    </div>
  );
}

SessionItem.propTypes = {
  session: PropTypes.shape({
    title: PropTypes.string,
    date: PropTypes.string,
    dateCategory: PropTypes.oneOf(Object.values(SESSION_DATE_CATEGORY)),
  }),
  handleSessionSelected: PropTypes.func,
};

export default SessionList;

SessionList.propTypes = {
  handleSessionSelected: PropTypes.func,
  // 是否只展示当前agent的session列表
  isCurrentAgentList: PropTypes.bool,
  currentAgentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
